import { NormalizedTokens } from './AuthenticationService';

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;
const DEFAULT_SCOPE = 'openid profile email offline_access';

const VERIFIER_KEY = 'rh_social_pkce_verifier';
const STATE_KEY = 'rh_social_pkce_state';

function webRedirectUri(): string {
  return `${window.location.origin}/callback`;
}

async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...Array.from(new Uint8Array(digest))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function generateState(): string {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...Array.from(array))).replace(/[+/=]/g, '');
}

export async function startSocialLoginRedirect(connection: string): Promise<void> {
  const verifier = await generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();

  window.sessionStorage.setItem(VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: AUTH0_CLIENT_ID!,
    redirect_uri: webRedirectUri(),
    response_type: 'code',
    scope: DEFAULT_SCOPE,
    audience: AUTH0_AUDIENCE!,
    connection,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.assign(`https://${AUTH0_DOMAIN}/authorize?${params}`);
}

export async function completePendingSocialLogin(): Promise<NormalizedTokens | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const error = params.get('error');

  if (!code && !error) return null;

  const verifier = window.sessionStorage.getItem(VERIFIER_KEY) || '';
  const expectedState = window.sessionStorage.getItem(STATE_KEY) || '';
  window.sessionStorage.removeItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(STATE_KEY);

  if (error) {
    cleanUrl();
    throw new Error(params.get('error_description') || 'Social sign-in failed.');
  }

  if (!code || !verifier || returnedState !== expectedState) {
    cleanUrl();
    return null;
  }

  const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: AUTH0_CLIENT_ID,
      code,
      redirect_uri: webRedirectUri(),
      code_verifier: verifier,
    }),
  });

  cleanUrl();

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error_description || 'Social sign-in failed.');
  if (!data.access_token || !data.id_token) return null;

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token ?? undefined,
  };
}

function cleanUrl(): void {
  try {
    window.history.replaceState({}, document.title, `${window.location.origin}/`);
  } catch {}
}
